import {
  StyleVariants,
  TableCellData,
  TableCellKinds,
  TableManualListItem,
} from '@site-mate/dashpivot-shared-library';

import { AttachmentColumn } from 'app/form-fields/attachment/register/attachment-column';
import { AttachmentColumComponent } from 'app/form-fields/attachment/register/attachment-column.component';
import { CategoryColumn } from 'app/form-fields/category/register/category-column';
import { CategoryColumnComponent } from 'app/form-fields/category/register/category-column.component';
import { CategoryHeaderColumn } from 'app/form-fields/category/register/category-header-column';
import { EmptyFormFieldColumnComponent } from 'app/form-fields/common/register/form-field-column/empty-form-field-column.component';
import { FormFieldColumn } from 'app/form-fields/common/register/form-field-column/form-field-column';
import { FormFieldColumnComponent } from 'app/form-fields/common/register/form-field-column/form-field-column.component';
import { FormFieldHeaderColumn } from 'app/form-fields/common/register/form-field-header/form-field-header-column';
import { DateFieldColumn } from 'app/form-fields/date/register/date-field-column';
import { TimeFieldColumn } from 'app/form-fields/date/register/time-field-column';
import { PhotoColumn } from 'app/form-fields/photo/register/photo-column';
import { PhotoColumnComponent } from 'app/form-fields/photo/register/photo-column.component';
import { SignatureArrayColumn } from 'app/form-fields/signature-array/register/signature-array-column';
import { SignatureArrayColumnComponent } from 'app/form-fields/signature-array/register/signature-array-column.component';

interface TableColumnData {
  columnItem: TableCellData;
  isPrefilled: boolean;
  header: FormFieldHeaderColumn;
  rowSpan: number;
  metadata?: Record<string, string>;
  isHeaderKindDifferentFromItemKind: boolean;
}

export class TableColumnsFactory {
  static resolver = {
    attachment: {
      Column: AttachmentColumn,
      renderer: AttachmentColumComponent,
    },
    list: {
      Column: CategoryColumn,
      renderer: CategoryColumnComponent,
    },
    photo: {
      Column: PhotoColumn,
      renderer: PhotoColumnComponent,
    },
    signature: {
      Column: SignatureArrayColumn,
      renderer: SignatureArrayColumnComponent,
    },
    manualSignature: {
      Column: SignatureArrayColumn,
      renderer: SignatureArrayColumnComponent,
    },
    date: {
      Column: DateFieldColumn,
      renderer: FormFieldColumnComponent,
    },
    time: {
      Column: TimeFieldColumn,
      renderer: FormFieldColumnComponent,
    },
  };

  private static defaultColumn = {
    Column: FormFieldColumn,
    renderer: FormFieldColumnComponent,
  };

  private static emptyFieldColumn = {
    Column: FormFieldColumn,
    Renderer: EmptyFormFieldColumnComponent,
  };

  static getTableColumn(tableColumnData: TableColumnData) {
    const { columnItem, isPrefilled, header, rowSpan, metadata, isHeaderKindDifferentFromItemKind } =
      tableColumnData;
    const kind = isPrefilled ? columnItem.kind : header.kind;
    const resolved = this.resolver[kind] || this.defaultColumn;

    let column;

    if (isHeaderKindDifferentFromItemKind) {
      column = new this.emptyFieldColumn.Column(columnItem, this.emptyFieldColumn.Renderer);
    } else if ((kind as TableCellKinds) === TableCellKinds.List) {
      column = this.listResolver(columnItem, header as CategoryHeaderColumn);
    } else {
      column = new resolved.Column(columnItem, resolved.renderer, metadata);
    }

    column.rowSpan = rowSpan;

    return column;
  }

  private static listResolver(columnItem: TableCellData, header: CategoryHeaderColumn) {
    const ListResolver = this.resolver.list;
    const convertedItem = TableColumnsFactory.convertListItemToCategory(
      columnItem as TableManualListItem & { manualListItems: { style: StyleVariants; value: string }[] },
    );

    return new ListResolver.Column(convertedItem, header, ListResolver.renderer);
  }

  private static convertListItemToCategory(
    item: TableManualListItem & { manualListItems: { style: StyleVariants; value: string }[] },
  ) {
    const selectedItems = [];

    const style =
      item.style || item.manualListItems?.find((listItem) => listItem.value === item.value)?.style;

    if (item.value) {
      selectedItems.push({ value: item.value, style });
    }
    return { ...item, selectedItems };
  }
}
